import MyInput from "../myInput";
import "./styles.scss";
import CampaignIcon from "@mui/icons-material/Campaign";
import omanEmoji from "../../assets/images/oman-emoji.svg";
import MyButton from "../myButton";
import { NavLink } from "react-router-dom";
import { useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Constants } from "../../utils/constants";
import DoneIcon from "@mui/icons-material/Done";
import { createAxiosCall } from "../../api/call";
import { generateOrderId } from "../../utils/numbers";

type FormErrors = {
	full_name?: string;
	email?: string;
	phone?: string;
	details?: string;
};

const DUPLICATE_SUBMIT_COOLDOWN_MS = 5 * 60 * 1000;
const FULL_NAME_MAX_LENGTH = 55;
const EMAIL_MAX_LENGTH = 55;
const DETAILS_MAX_LENGTH = 65535;

async function sha256Hex(value: string): Promise<string> {
	const cryptoApi = window.crypto || (window as any).msCrypto;
	const subtle = cryptoApi?.subtle;
	const bytes = new TextEncoder().encode(value);

	if (!subtle) {
		return Array.from(bytes)
			.map((byte) => byte.toString(16).padStart(2, "0"))
			.join("");
	}

	const hashBuffer = await subtle.digest("SHA-256", bytes);
	return Array.from(new Uint8Array(hashBuffer))
		.map((byte) => byte.toString(16).padStart(2, "0"))
		.join("");
}

async function getDuplicateSubmitStorageKey(params: { email: string; phone: string; source: string }): Promise<string> {
	const identity = [params.source.trim().toLowerCase(), params.email.trim().toLowerCase(), params.phone.trim()].join("|");
	const hash = await sha256Hex(identity);
	return `campaign-submit:${hash}`;
}

function getRecentSubmit(key: string): { submittedAt: number; orderId?: string } | null {
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return null;

		const parsed = JSON.parse(raw);
		const submittedAt = Number(parsed?.submittedAt);

		if (!Number.isFinite(submittedAt) || submittedAt <= 0) {
			return null;
		}

		return {
			submittedAt,
			orderId: typeof parsed?.orderId === "string" ? parsed.orderId : undefined,
		};
	} catch {
		return null;
	}
}

function rememberSuccessfulSubmit(key: string, orderId: string): void {
	try {
		window.localStorage.setItem(
			key,
			JSON.stringify({
				submittedAt: Date.now(),
				orderId,
			}),
		);
	} catch {}
}

export default function MyForm() {
	const formRef = useRef<HTMLFormElement | null>(null);
	const submittingRef = useRef(false);
	const [errors, setErrors] = useState<FormErrors>({});
	const [submitted, setSubmitted] = useState(false);
	const [orderNum, setOrderNum] = useState("");
	const [loading, setLoading] = useState(false);

	const validateForm = ({ full_name, email, phone, details }: { full_name: string; email: string; phone: string; details: string }): FormErrors => {
		const errors: FormErrors = {};

		if (!full_name || full_name.trim().length === 0) {
			errors.full_name = "الرجاء إدخال الاسم الكامل";
		} else if (full_name.length > FULL_NAME_MAX_LENGTH) {
			errors.full_name = `الاسم الكامل يجب ألا يتجاوز ${FULL_NAME_MAX_LENGTH} حرفاً`;
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
		if (!email || !emailRegex.test(email)) {
			errors.email = "الرجاء إدخال بريد إلكتروني صحيح";
		} else if (email.length > EMAIL_MAX_LENGTH) {
			errors.email = `البريد الإلكتروني يجب ألا يتجاوز ${EMAIL_MAX_LENGTH} حرفاً`;
		}

		const phoneRegex = new RegExp(`^\\d{${Constants.phoneNumberLen}}$`);
		if (!phone || !phoneRegex.test(phone)) {
			errors.phone = `الرجاء إدخال رقم هاتف مكوّن من ${Constants.phoneNumberLen} أرقام`;
		}

		if (details.length > DETAILS_MAX_LENGTH) {
			errors.details = `تفاصيل الشكوى يجب ألا تتجاوز ${DETAILS_MAX_LENGTH} حرفاً`;
		}

		return errors;
	};

	const onSubmit = async (e: any) => {
		e.preventDefault();

		if (loading || submittingRef.current) return;

		submittingRef.current = true;

		const form = e.currentTarget;

		const full_name = String(form.full_name.value || "").trim();
		const email = String(form.email.value || "").trim();
		const phone = String(form.phone.value || "").trim();
		const details = String(form.details.value || "");

		const validationErrors = validateForm({ full_name, email, phone, details });

		setErrors(validationErrors);

		if (Object.keys(validationErrors).length > 0) {
			enqueueSnackbar("يرجى تصحيح الأخطاء", { variant: "error" });
			submittingRef.current = false;
			return;
		}

		const full_phone = Constants.phoneNumberCountryCode + phone;

		setLoading(true);

		try {
			const orderId = generateOrderId();
			const duplicateSubmitKey = await getDuplicateSubmitStorageKey({
				email,
				phone: full_phone,
				source: Constants.source,
			});
			const recentSubmit = getRecentSubmit(duplicateSubmitKey);

			if (recentSubmit && Date.now() - recentSubmit.submittedAt < DUPLICATE_SUBMIT_COOLDOWN_MS) {
				enqueueSnackbar("تم إرسال هذا الطلب مسبقاً، يرجى الانتظار قليلاً قبل المحاولة مرة أخرى", { variant: "warning" });
				return;
			}

			await createAxiosCall({
				method: "POST",
				host: "https://formcarry.com",
				endpoint: "s/UwmsbOw2E4Z",
				data: {
					full_name,
					email,
					phone: full_phone,
					details,
					owner: Constants.owner,
					source: Constants.source,
					order_id: orderId,
				},
			});

			rememberSuccessfulSubmit(duplicateSubmitKey, orderId);

			if (typeof window !== "undefined" && (window as any).gtag) {
				(window as any).gtag("event", "conversion", {
					send_to: "AW-18092238826/Gz51CJnh86IcEOrPhrND",
				});
			}

			enqueueSnackbar("تم استلام طلب المساعدة بنجاح", { variant: "success" });

			setOrderNum(orderId);

			setSubmitted(true);
		} catch (error: any) {
			const message = "حدث خطأ أثناء إرسال طلب المساعدة";
			enqueueSnackbar(message, { variant: "error" });
		} finally {
			setLoading(false);
			submittingRef.current = false;
		}
	};

	if (submitted) {
		return (
			<div className="form-success-container">
				<div className="form-success-card">
					<DoneIcon className="form-success-icon" />
					<p className="success-title">تم استلام طلب المساعدة بنجاح</p>
					<p className="success-subtitle">سنراجع بياناتك ونتواصل معك. هذا الإرسال لا يعني تقديم الشكوى إلى أي جهة رسمية.</p>
					<p className="success-order-number">
						رقم الطلب: <strong>{orderNum}</strong>
					</p>

					<MyButton
						onClick={() => {
							setSubmitted(false);
							setErrors({});
							setOrderNum("");
							formRef.current?.reset();
						}}
					>
						طلب مساعدة جديد
					</MyButton>
				</div>
			</div>
		);
	}

	return (
		<form ref={formRef} id="form-card" className="form-card-container" onSubmit={onSubmit}>
			<div className="form-header">
				<div className="form-title-wrapper">
					<CampaignIcon className="form-title-icon" />
					<p className="form-title">طلب مساعدة في إعداد شكوى</p>
				</div>

				<p className="form-subtitle">أرسل بياناتك الآن وسنتواصل معك لمراجعة الطلب والخطوات التالية</p>
				<p className="form-compliance-note">
					خدمة فردية مستقلة لمساعدة العملاء في إعداد وتنظيم معلومات الشكاوى فقط. ليست خدمة قانونية مرخصة أو بوابة حكومية، ولا نطلب كلمات مرور أو بيانات دفع.
				</p>
			</div>
			<div className="form-inputs-container">
				<MyInput
					label="الاسم الكامل"
					dir="rtl"
					type="text"
					variant="standard"
					name="full_name"
					error={!!errors.full_name}
					helperText={errors.full_name}
					onChange={() => {
						setErrors((prev) => ({ ...prev, full_name: undefined }));
					}}
					slotProps={{ htmlInput: { maxLength: FULL_NAME_MAX_LENGTH } }}
					required
					disabled={loading}
				/>
				<MyInput
					label="البريد الإلكتروني"
					dir="rtl"
					type="email"
					variant="standard"
					name="email"
					error={!!errors.email}
					helperText={errors.email}
					onChange={() => {
						setErrors((prev) => ({ ...prev, email: undefined }));
					}}
					slotProps={{ htmlInput: { maxLength: EMAIL_MAX_LENGTH } }}
					required
					disabled={loading}
				/>
				<div className="form-phone-number-container">
					<div className={`phone-number-country-code-container${!!errors.phone ? " error" : ""}`}>
						<img className="phone-number-country-flag" src={omanEmoji} alt="" />
						<label className="phone-number-country-code">{Constants.phoneNumberCountryCode}</label>
					</div>
					<MyInput
						className="form-phone-input"
						placeholder="XXXXXXXX"
						dir="ltr"
						type="tel"
						inputMode="numeric"
						variant="standard"
						name="phone"
						error={!!errors.phone}
						helperText={errors.phone}
						onChange={(e) => {
							let value = e.target.value;
							value = value.replace(/\D/g, "");
							value = value.slice(0, Constants.phoneNumberLen);
							e.target.value = value;

							setErrors((prev) => ({ ...prev, phone: undefined }));
						}}
						required
						disabled={loading}
					/>
				</div>
				<MyInput
					label="تفاصيل الطلب أو موضوع الشكوى"
					dir="rtl"
					type="text"
					variant="filled"
					name="details"
					multiline
					minRows={5}
					error={!!errors.details}
					helperText={errors.details}
					onChange={() => {
						setErrors((prev) => ({ ...prev, details: undefined }));
					}}
					slotProps={{ htmlInput: { maxLength: DETAILS_MAX_LENGTH } }}
					disabled={loading}
				/>
			</div>
			<div className="form-actions">
				<MyButton type="submit" className="form-submit-button" disabled={loading}>
					{loading ? "جارٍ إرسال الطلب..." : "إرسال طلب المساعدة"}
				</MyButton>
			</div>
			<p className="form-legal-links">
				بالإرسال أنت تؤكد فهمك لطبيعة الخدمة المحدودة وتوافق على <NavLink to="/terms-of-use">شروط الاستخدام</NavLink> و{" "}
				<NavLink to="/privacy-policy">سياسة الخصوصية</NavLink>.
			</p>
		</form>
	);
}
